import { useEffect, useCallback, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, Link } from "react-router-dom";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { mobileMenuVariants, overlayVariants } from "@/lib/motion";
import { SidebarSection } from "./SidebarSection";
import { SidebarItem } from "./SidebarItem";
import { mainNavSections, utilityNavItems } from "./sidebarData";
import { useNavPermissions } from "./useNavPermissions";
import IconButton from "@/components/common/GoogleIconButton";

interface MobileSidebarProps {
  open: boolean;
  onClose: () => void;
}

export function MobileSidebar({ open, onClose }: MobileSidebarProps) {
  const location = useLocation();
  const { canViewItem, homePath } = useNavPermissions();

  const visibleUtilityItems = useMemo(
    () => utilityNavItems.filter(canViewItem),
    [canViewItem],
  );

  const prevPathname = useRef(location.pathname);
  const sidebarRef = useRef<HTMLElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const desktop = window.matchMedia("(min-width: 1024px)");
    const closeOnDesktop = () => {
      if (desktop.matches) onClose();
    };
    desktop.addEventListener("change", closeOnDesktop);
    return () => desktop.removeEventListener("change", closeOnDesktop);
  }, [onClose]);

  useEffect(() => {
    if (prevPathname.current !== location.pathname) {
      prevPathname.current = location.pathname;
      onClose();
    }
  }, [location.pathname, onClose]);

  useEffect(() => {
    if (open) {
      const previouslyFocused = document.activeElement as HTMLElement | null;
      document.body.style.overflow = "hidden";
      requestAnimationFrame(() => closeButtonRef.current?.focus());
      return () => {
        document.body.style.overflow = "";
        previouslyFocused?.focus();
      };
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape" && open) {
        onClose();
        return;
      }
      if (e.key !== "Tab" || !open || !sidebarRef.current) return;
      const focusable = Array.from(
        sidebarRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [open, onClose],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="fixed inset-0 z-40 bg-backdrop lg:hidden"
            onClick={onClose}
          />

          <motion.aside
            ref={sidebarRef}
            role="dialog"
            aria-modal="true"
            aria-label="Navigation menu"
            variants={mobileMenuVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className={cn(
              "fixed inset-y-0 left-0 z-50 flex w-[280px] flex-col",
              "bg-sidebar border-r border-sidebar-border shadow-overlay",
              "lg:hidden",
            )}
          >
            <div className="flex items-center justify-between h-14 px-4 border-b border-sidebar-border">
              <Link
                to={homePath}
                onClick={onClose}
                aria-label={
                  homePath === "/home" ? "Go to home" : "Go to customers"
                }
                className="flex items-center gap-2.5 rounded-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary focus-visible:shadow-none"
              >
                <img
                  src="/planetwise-logo.png"
                  alt="Planetwise"
                  className="h-8 w-8"
                />
                <span className="text-sm font-semibold text-sidebar-foreground tracking-tight">
                  Planetwise
                </span>
              </Link>
              <IconButton
                ref={closeButtonRef}
                onClick={onClose}
                aria-label="Close menu"
                sx={{
                  padding: "0.375rem",
                  borderRadius: "0.5rem",
                  color: "var(--sidebar-foreground-40)",
                  "&:hover": {
                    backgroundColor: "var(--sidebar-accent)",
                    color: "var(--sidebar-foreground)",
                  },
                }}
              >
                <X size={18} />
              </IconButton>
            </div>

            <nav
              aria-label="Main navigation"
              className="flex-1 overflow-y-auto px-3 py-4 space-y-5"
            >
              {mainNavSections.map((section) => (
                <SidebarSection
                  key={section.title ?? section.items[0]?.path}
                  section={section}
                  collapsed={false}
                />
              ))}
            </nav>

            <div className="border-t border-sidebar-border px-3 py-3 space-y-0.5">
              {visibleUtilityItems.map((item) => (
                <SidebarItem
                  key={item.path}
                  item={item}
                  collapsed={false}
                  isActive={
                    location.pathname === item.path ||
                    location.pathname.startsWith(item.path + "/")
                  }
                />
              ))}
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
